import { useEffect, useState } from 'react';

import { useDashboardStore } from '@/state/store';
import { EventType, type AnyEnvelope } from '@/events';

/**
 * Risk Officer cameo overlay.
 *
 * When a ``risk.state_changed`` event flips a pod to ``frozen``, a
 * big stern-faced Risk Officer character slides in from the right
 * edge of the building view for ~4 s, carrying the affected pod's
 * name and the freeze reason.  Mirrors the Scene 2 raskadrovka in
 * ``PodPlatform/docs/DASHBOARD_EVENT_SEQUENCES.md``:
 *
 *   "Risk Officer storms into room
 *    Red flashing border
 *    Audio: door slam
 *    Camera: focus on target pod
 *    Duration: 4 seconds"
 *
 * Pure DOM/CSS so it doesn't fight the Pixi GL context.  The
 * subscription compares the LAST envelope id (not feed length --
 * the feed is a bounded ring buffer that stops growing at 200), the
 * same fix the PixiCanvas event hook uses.
 */

interface ActiveCameo {
  id: string;
  podName: string;
  reason: string;
  enteredAt: number;
}

const CAMEO_DURATION_MS = 4_000;

export function RiskOfficerCameo() {
  const [cameo, setCameo] = useState<ActiveCameo | null>(null);

  useEffect(() => {
    let lastSeenId: string | null = null;
    const unsubscribe = useDashboardStore.subscribe((state) => {
      const env: AnyEnvelope | undefined = state.feed[state.feed.length - 1];
      if (!env) return;
      if (env.id === lastSeenId) return;
      lastSeenId = env.id;
      if (env.type !== EventType.RISK_STATE_CHANGED) return;
      const to = String(env.data.to ?? '').toLowerCase();
      if (to !== 'frozen') return;
      const podId = env.subject.startsWith('pod:')
        ? env.subject.slice('pod:'.length)
        : env.subject;
      // Resolve a friendly pod name from the building snapshot.
      const summary = state.building?.floors
        .flatMap((f) => f.pods)
        .find((p) => p.pod_id === podId);
      const reasonRaw = String(env.data.reason ?? '');
      setCameo({
        id: env.id,
        podName: summary?.name ?? podId,
        reason: prettyReason(reasonRaw),
        enteredAt: Date.now(),
      });
    });
    return () => unsubscribe();
  }, []);

  // Auto-dismiss after CAMEO_DURATION_MS.
  useEffect(() => {
    if (!cameo) return;
    const remaining = Math.max(0, cameo.enteredAt + CAMEO_DURATION_MS - Date.now());
    const id = window.setTimeout(() => setCameo(null), remaining);
    return () => window.clearTimeout(id);
  }, [cameo]);

  if (!cameo) return null;

  return (
    <div
      role="status"
      aria-live="assertive"
      style={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: 'min(420px, 70%)',
        background: 'linear-gradient(270deg, rgba(248, 81, 73, 0.92), rgba(248, 81, 73, 0.0))',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '24px 28px',
        pointerEvents: 'none',
        animation: 'riskOfficerSlide 0.4s ease-out',
        zIndex: 4,
      }}
    >
      <RiskOfficerAvatar />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            color: '#ffd9d6',
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            marginBottom: 4,
          }}
        >
          ⚠ Risk Officer
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: '#ffffff',
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            marginBottom: 6,
          }}
        >
          FROZEN
        </div>
        <div
          style={{
            fontSize: 14,
            color: '#fff',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 600,
          }}
          title={cameo.podName}
        >
          {cameo.podName}
        </div>
        <div
          style={{
            fontSize: 11,
            color: '#ffd9d6',
            marginTop: 4,
          }}
          title={cameo.reason}
        >
          {cameo.reason}
        </div>
      </div>
    </div>
  );
}

function RiskOfficerAvatar() {
  return (
    <div
      aria-hidden
      style={{
        width: 92,
        height: 92,
        borderRadius: '50%',
        background: '#f2e8c9',
        border: '4px solid #ffffff',
        position: 'relative',
        flexShrink: 0,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
      }}
    >
      {/* Warning triangle hat */}
      <div
        style={{
          position: 'absolute',
          top: -22,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '14px solid transparent',
          borderRight: '14px solid transparent',
          borderBottom: '22px solid #f85149',
          filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.4))',
        }}
      />
      <span
        style={{
          position: 'absolute',
          top: -19,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: 12,
          fontWeight: 800,
          color: 'white',
        }}
      >
        !
      </span>
      {/* Angled eyebrows */}
      <span
        style={{
          position: 'absolute',
          top: 24,
          left: 18,
          width: 18,
          height: 3,
          background: '#0d1117',
          borderRadius: 1.5,
          transform: 'rotate(15deg)',
        }}
      />
      <span
        style={{
          position: 'absolute',
          top: 24,
          right: 18,
          width: 18,
          height: 3,
          background: '#0d1117',
          borderRadius: 1.5,
          transform: 'rotate(-15deg)',
        }}
      />
      {/* Eyes */}
      <span
        style={{
          position: 'absolute',
          top: 32,
          left: 22,
          width: 7,
          height: 7,
          background: '#0d1117',
          borderRadius: '50%',
        }}
      />
      <span
        style={{
          position: 'absolute',
          top: 32,
          right: 22,
          width: 7,
          height: 7,
          background: '#0d1117',
          borderRadius: '50%',
        }}
      />
      {/* Frowning mouth */}
      <span
        style={{
          position: 'absolute',
          top: 58,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 36,
          height: 14,
          borderTop: '3px solid #0d1117',
          borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
        }}
      />
    </div>
  );
}

function prettyReason(raw: string): string {
  if (!raw) return 'risk limits breached';
  // Trim common prefixes from backend reason strings.
  return raw
    .replace(/^hard_breach\((.+)\)$/, 'hard breach: $1')
    .replace(/^soft_breach\((.+)\)$/, 'soft breach: $1')
    .replace(/_/g, ' ');
}
